import React, { useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { useFormFields } from '../../util/hooks';
import axios from 'axios';
import { useAuth } from '../../util/authContext';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff, Key, Email, Password } from '@mui/icons-material';
import { formStyles } from '../../util/styles';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import { Card, CardActions, CardContent, Typography, Tabs, Tab, Box, CircularProgress } from '@mui/material';
import MfaSetupModal from '../Account/Modals/MfaSetupModal';
import { startAuthentication } from '@simplewebauthn/browser';
import { decodeBase64 } from '../../util/keyManager';

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const SignInForm = () => {
  const classes = formStyles();
  const [fields, handleFieldChange] = useFormFields({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState("");
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [preferredMfaChannel, setPreferredMfaChannel] = useState(null);
  const [allowedMfaChannels, setAllowMfaChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaMessage, setMfaMessage] = useState(null);
  const [configureMfa, setConfigureMfa] = useState(false);
  const [mfaPromptOpen, setMfaPromptOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [errors, setErrors] = useState([]);
  
  // Auth method tab state
  const [authMethod, setAuthMethod] = useState(0); // 0: password, 1: passwordless, 2: passkey
  
  // Passwordless state
  const [passwordlessEmail, setPasswordlessEmail] = useState('');
  const [passwordlessCode, setPasswordlessCode] = useState('');
  const [passwordlessStep, setPasswordlessStep] = useState('email'); // 'email' | 'code'
  
  // Passkey state
  const [passkeyEmail, setPasskeyEmail] = useState('');

  const { createSession } = useAuth();

  const handleTokenChange = (e) => {
    let tmpToken = e.target.value;
    setToken(tmpToken);
    if (tmpToken.length === 6) {
      setTimeout(() => {
        handlePasswordSubmit(null, tmpToken);
      }, 300);
    }
  };

  const getUserInfo = async () => {
    setIsLoading(true);
    let tmpUserData = null;
    try {
      const result = await axios.get(
        `/api/user?${new URLSearchParams({ email: fields.email, password: fields.password }).toString()}`
      );
      if (result && result.data) {
        tmpUserData = result.data;
        setUserInfo(result.data);
      }
    } catch (e) {
      console.error('Error while getting user info for MFA setup: ', e);
      setErrors(['Failed to get user info. Please check your password']);
    }
    setIsLoading(false);
    return tmpUserData;
  };

  const sendSms = async (event) => {
    event.preventDefault();
    await sendMfaToken('sms');
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    await sendMfaToken('email');
  };

  const sendMfaToken = async (channel) => {
    setPreferredMfaChannel(channel);
    try {
      setIsLoading(true);
      setMfaMessage('Sending...');
      let res = await axios.post('/api/mfa/send-token', {
        email: fields.email,
        channel: channel,
      });

      if (res && res.data.status) {
        setMfaMessage('Token sent via ' + channel + '!');
      }
    } catch (error) {
      console.error('Failed to send MFA token via ' + channel, error);
      setMfaMessage('An error occurred.');
      setErrors(['Failed to send MFA token via ' + channel]);
    }
    setIsLoading(false);
  };

  // Handle successful login
  // subscriptionStatus is returned at the top level of the sign-in response
  // (sibling to `user`), not on the user object itself - keep it a separate
  // param rather than folding it into userData.
  const handleLoginSuccess = async (userData, expires, subscriptionStatus, prfParams = null) => {
    await createSession(
      {
        name: userData.name,
        email: userData.email,
        groupsLastUpdated: userData.groupsLastUpdated,
        mfaEnabled: userData.mfaEnabled,
        companyRequiresMfa: userData.companyRequiresMfa,
        preferredMfa: userData.preferredMfa,
        subscriptionStatus
      },
      expires,
      prfParams
    );
  };

  // Password-based sign in
  const handlePasswordSubmit = async (event, tmpToken = null, tmpChannel = null) => {
    if (event) event.preventDefault();

    let hasError = false, signInRes = null;

    try {
      setErrors([]);
      setIsLoading(true);
      let payload = {
        email: fields.email,
        password: fields.password,
      };

      if (showTokenInput || tmpToken || tmpChannel) {
        payload.token = tmpToken || token;
        payload.channel = tmpChannel || preferredMfaChannel;
      }

      let res = await axios.post('/api/signin', payload);

      if (res && res.status === 200 && res.data.user) {
        if (typeof gtag !== 'undefined') {
          gtag("event", "login", { method: payload.token ? "MFA" : "Password" });
        }
        await handleLoginSuccess(res.data.user, res.data.expires, res.data.subscriptionStatus);
        return;
      } else {
        hasError = true;
        signInRes = res;
      }
    } catch (error) {
      hasError = true;
      if (error.response) {
        signInRes = error.response;
      }
      if (!error.response?.data?.error) {
        setErrors([error.message]);
      }
    }

    if (hasError && signInRes?.data) {
      const { error, errorType, channel, allowedChannels, usePasswordless } = signInRes.data;
      
      if (usePasswordless) {
        setErrors(['This account uses passwordless login. Please use the "Email Code" tab.']);
        setPasswordlessEmail(fields.email);
        setAuthMethod(1);
      } else if (error === 'Username/password is invalid') {
        setErrors(['The username/password entered is incorrect. Please try again.']);
      } else if (error === 'Missing MFA token') {
        setShowTokenInput(true);
        let message = 'Please provide the token.';
        switch (channel) {
          case 'authenticator': message += " Check your authenticator app."; break;
          case 'sms': message += " A text message has been sent with the token."; break;
          case 'email': message += " An email has been sent with the token."; break;
        }
        setMfaMessage(message);
        setAllowMfaChannels(allowedChannels || []);
        setPreferredMfaChannel(channel);
      } else if (error === 'Invalid MFA token') {
        setErrors(['The MFA token entered is incorrect. Please try again.']);
        setMfaMessage('Token is invalid');
      } else if (errorType === "MFA_REQUIRED_BY_COMPANY") {
        await getUserInfo();
        setMfaPromptOpen(true);
        setConfigureMfa(true);
      } else {
        setErrors([error]);
      }
    }

    setIsLoading(false);
  };

  // Passwordless sign in - Step 1: Request code
  const handlePasswordlessInitiate = async (event) => {
    if (event) event.preventDefault();
    
    const email = (passwordlessEmail || '').toLowerCase().trim();
    if (!email || !email.includes('@')) {
      setErrors(['Please enter a valid email address']);
      return;
    }

    setErrors([]);
    setIsLoading(true);

    try {
      const res = await axios.post('/api/passwordless/initiate', { email });
      
      if (res.data.error) {
        setErrors([res.data.error]);
      } else {
        setPasswordlessStep('code');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'An error occurred';
      if (error.response?.data?.errorType === 'EMAIL_NOT_REGISTERED') {
        setErrors(['No account found with this email address.']);
      } else if (errorMsg.includes('not enabled')) {
        setErrors(['Passwordless login is not enabled for this account. Please use password login.']);
      } else {
        setErrors([errorMsg]);
      }
    }

    setIsLoading(false);
  };

  // Passwordless sign in - Step 2: Verify code
  const handlePasswordlessVerify = async (event) => {
    if (event) event.preventDefault();
    
    if (!passwordlessCode || passwordlessCode.length < 6) {
      setErrors(['Please enter the verification code']);
      return;
    }

    setErrors([]);
    setIsLoading(true);

    try {
      const res = await axios.post('/api/passwordless/signin', {
        email: passwordlessEmail.toLowerCase().trim(),
        code: passwordlessCode,
      });

      if (res.data.user) {
        if (typeof gtag !== 'undefined') {
          gtag("event", "login", { method: "Passwordless" });
        }
        await handleLoginSuccess(res.data.user, res.data.expires, res.data.subscriptionStatus);
      } else {
        setErrors([res.data.error || 'Login failed']);
      }
    } catch (error) {
      const errorType = error.response?.data?.errorType;
      if (errorType === 'PASSWORDLESS_LOGIN_CODE_INVALID') {
        setErrors(['Invalid verification code. Please try again.']);
      } else if (errorType === 'PASSWORDLESS_LOGIN_CODE_EXPIRED') {
        setErrors(['Verification code expired. Please request a new one.']);
        setPasswordlessStep('email');
        setPasswordlessCode('');
      } else {
        setErrors([error.response?.data?.error || error.message]);
      }
    }

    setIsLoading(false);
  };

  // Auto-submit when passwordless code reaches 6 digits
  const handlePasswordlessCodeChange = (e) => {
    const code = e.target.value;
    setPasswordlessCode(code);
    if (code.length === 6) {
      setTimeout(() => handlePasswordlessVerify(null), 300);
    }
  };

  // WebAuthn/Passkey sign in
  const handlePasskeyLogin = async (event) => {
    if (event) event.preventDefault();
    
    const email = (passkeyEmail || '').toLowerCase().trim();
    if (!email || !email.includes('@')) {
      setErrors(['Please enter your email address']);
      return;
    }

    setErrors([]);
    setIsLoading(true);

    try {
      // Step 1: Get authentication options from server (includes PRF eval if user has prfSalt)
      const optionsRes = await axios.post('/api/webauthn/auth/options', { email });
      const options = optionsRes.data;

      // The browser library only decodes challenge/allowCredentials for us —
      // the PRF salt must be converted from base64url to a BufferSource ourselves.
      const prfSaltFirst = options.extensions?.prf?.eval?.first;
      if (prfSaltFirst) {
        options.extensions.prf.eval.first = decodeBase64(prfSaltFirst);
      }

      // Step 2: Start WebAuthn authentication (browser prompt)
      const authResponse = await startAuthentication({ optionsJSON: options });

      // Step 3: Verify with server
      const verifyRes = await axios.post('/api/webauthn/auth/verify', {
        email,
        response: authResponse,
      });

      if (verifyRes.data.user) {
        if (typeof gtag !== 'undefined') {
          gtag("event", "login", { method: "Passkey" });
        }

        // Extract PRF output if the authenticator supported it
        const prfFirst = authResponse.clientExtensionResults?.prf?.results?.first;
        const prfParams = prfFirst ? {
          prfOutput: prfFirst,
          prfCredentialId: authResponse.id,
          prfWrappedDek: verifyRes.data.prfWrappedDek || null,
          prfSalt: verifyRes.data.prfSalt || null,
        } : null;

        await handleLoginSuccess(verifyRes.data.user, verifyRes.data.expires, verifyRes.data.subscriptionStatus, prfParams);
      } else {
        setErrors(['Passkey authentication failed']);
      }
    } catch (error) {
      console.error('Passkey login error:', error);
      
      // Handle WebAuthn browser errors
      if (error.name === 'NotAllowedError') {
        setErrors(['Authentication was cancelled or timed out. Please try again.']);
      } else if (error.name === 'SecurityError') {
        setErrors(['Security error. Please ensure you are using HTTPS.']);
      } else if (error.response?.data?.errorType === 'WEBAUTHN_CREDENTIAL_NOT_FOUND') {
        setErrors(['No passkeys found for this account. Please use password or email code login.']);
      } else if (error.response?.data?.errorType === 'WEBAUTHN_NOT_SUPPORTED') {
        setErrors(['Passkeys are not enabled for this account.']);
      } else if (error.response?.data?.errorType === 'EMAIL_NOT_REGISTERED') {
        setErrors(['No account found with this email address.']);
      } else {
        setErrors([error.response?.data?.error || error.message || 'Passkey authentication failed']);
      }
    }

    setIsLoading(false);
  };

  // Render password login form
  const renderPasswordForm = () => (
    <form className={classes.form} noValidate onSubmit={handlePasswordSubmit}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            id="email"
            name="email"
            label="Email"
            autoComplete="email"
            autoFocus
            value={fields.email}
            onChange={handleFieldChange}
            InputProps={{ classes: { root: classes.outlinedRoot } }}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            name="password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            label="Password"
            value={fields.password}
            onChange={handleFieldChange}
            InputProps={{
              classes: { root: classes.outlinedRoot },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    size="large"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {showTokenInput && (
          <Card style={{ width: '100%', margin: '7px' }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Multi-factor Authentication
              </Typography>
              <Grid size={12}>
                {mfaMessage && <Typography variant="body2">{mfaMessage}</Typography>}
                <TextField
                  variant="outlined"
                  fullWidth
                  id="token"
                  name="token"
                  label="Token"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                  value={token}
                  onChange={handleTokenChange}
                  InputProps={{ classes: { root: classes.outlinedRoot } }}
                  style={{ marginTop: '7px' }}
                />
              </Grid>
            </CardContent>
            <CardActions>
              <Grid size={12}>
                <Grid container>
                  <Grid size={3}><hr /></Grid>
                  <Grid size={6} style={{ textAlign: 'center' }}> Having trouble? </Grid>
                  <Grid size={3}><hr /></Grid>
                </Grid>
                {allowedMfaChannels.includes('sms') && (
                  <Button type="button" color="primary" onClick={sendSms} disabled={isLoading}>
                    {preferredMfaChannel === 'sms' ? 'Re-send SMS' : 'Send SMS'}
                  </Button>
                )}
                <Button type="button" color="primary" onClick={sendEmail} disabled={isLoading}>
                  {preferredMfaChannel === 'email' ? 'Re-send Email' : 'Send Email'}
                </Button>
              </Grid>
            </CardActions>
          </Card>
        )}
        
        <Grid size={12}>
          <Link href="/forgotpassword" variant="body1">Forgot Password?</Link>
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading}
            className={classes.submit}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  // Render passwordless login form
  const renderPasswordlessForm = () => (
    <form className={classes.form} noValidate onSubmit={passwordlessStep === 'email' ? handlePasswordlessInitiate : handlePasswordlessVerify}>
      <Grid container spacing={2}>
        {passwordlessStep === 'email' ? (
          <>
            <Grid size={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                We'll send a verification code to your email address.
              </Typography>
            </Grid>
            <Grid size={12}>
              <TextField
                variant="outlined"
                fullWidth
                id="passwordless-email"
                name="email"
                label="Email"
                autoComplete="email"
                autoFocus
                value={passwordlessEmail}
                onChange={(e) => setPasswordlessEmail(e.target.value)}
                InputProps={{ classes: { root: classes.outlinedRoot } }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                className={classes.submit}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Send Code'}
              </Button>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={12}>
              <Alert severity="info">
                We've sent a verification code to <strong>{passwordlessEmail}</strong>
              </Alert>
            </Grid>
            <Grid size={12}>
              <TextField
                variant="outlined"
                fullWidth
                id="passwordless-code"
                name="code"
                label="Verification Code"
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
                value={passwordlessCode}
                onChange={handlePasswordlessCodeChange}
                InputProps={{ classes: { root: classes.outlinedRoot } }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                className={classes.submit}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify & Sign In'}
              </Button>
            </Grid>
            <Grid size={12} style={{ display: 'flex', gap: 8 }}>
              <Button
                type="button"
                variant="text"
                color="primary"
                size="small"
                disabled={isLoading}
                onClick={() => {
                  setPasswordlessCode('');
                  setErrors([]);
                  handlePasswordlessInitiate(null);
                }}
              >
                Resend Code
              </Button>
              <Button
                type="button"
                variant="text"
                color="primary"
                size="small"
                onClick={() => {
                  setPasswordlessStep('email');
                  setPasswordlessCode('');
                  setErrors([]);
                }}
              >
                Use a different email
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </form>
  );

  // Render passkey login form
  const renderPasskeyForm = () => (
    <form className={classes.form} noValidate onSubmit={handlePasskeyLogin}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Sign in securely with your passkey (Face ID, Touch ID, Windows Hello, or security key).
          </Typography>
        </Grid>
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            id="passkey-email"
            name="email"
            label="Email"
            autoComplete="email webauthn"
            autoFocus
            value={passkeyEmail}
            onChange={(e) => setPasskeyEmail(e.target.value)}
            InputProps={{ classes: { root: classes.outlinedRoot } }}
          />
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading}
            className={classes.submit}
            startIcon={!isLoading && <Key />}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign In with Passkey'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  return (
    <>
      <Collapse in={errors && errors.length > 0}>
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <div>
            {errors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        </Alert>
      </Collapse>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs 
          value={authMethod} 
          onChange={(e, newValue) => {
            setAuthMethod(newValue);
            setErrors([]);
            // Reset passwordless state when switching away
            if (newValue !== 1) {
              setPasswordlessStep('email');
              setPasswordlessCode('');
            }
          }}
          variant="fullWidth"
        >
          <Tab icon={<Password />} label="Password" />
          <Tab icon={<Email />} label="Email Code" />
          <Tab icon={<Key />} label="Passkey" />
        </Tabs>
      </Box>

      <TabPanel value={authMethod} index={0}>
        {renderPasswordForm()}
      </TabPanel>
      <TabPanel value={authMethod} index={1}>
        {renderPasswordlessForm()}
      </TabPanel>
      <TabPanel value={authMethod} index={2}>
        {renderPasskeyForm()}
      </TabPanel>

      <Grid container style={{ marginTop: 16 }}>
        <Grid size={12}>
          <Link href="/signup" variant="body1">
            Don't have an account? Sign Up
          </Link>
        </Grid>
        <Grid size={12} style={{ marginTop: 8 }}>
          <Typography variant="body2">
            Email us at <Link href="mailto:support@juryboxapp.com">support@juryboxapp.com</Link> for support
          </Typography>
        </Grid>
      </Grid>

      {configureMfa && userInfo !== null && (
        <MfaSetupModal
          key="mfa-prompt-modal"
          open={mfaPromptOpen}
          onComplete={(mfaToken, channel) => {
            setMfaPromptOpen(false);
            setShowTokenInput(true);

            if (channel && channel === "email" && !mfaToken) {
              sendMfaToken('email');
            }

            if (mfaToken) {
              if (channel) setPreferredMfaChannel(channel);
              setToken(mfaToken);
              handlePasswordSubmit(null, mfaToken, channel);
            }
          }}
          loginInfo={{ email: fields.email, password: fields.password }}
          phone={userInfo.phone || ''}
          preferredMfa={userInfo.preferredMfa}
          requireMfa={userInfo.companyRequiresMfa}
          mfaEnabled={userInfo.mfaEnabled}
        />
      )}
    </>
  );
};

export default SignInForm;
